
import { addDays, startOfDay, parseISO, addMonths, isBefore, isAfter, closestTo, isValid, isSameDay } from 'date-fns';
import { Event } from '@/lib/types';

// Helper function to check if two dates are the same day
export const isSameDayHelper = (date1: Date, date2: Date) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Generate array of dates to display in the calendar
export const generateCalendarDays = (startDate: Date, count: number): Date[] => {
  return Array.from({ length: count }, (_, i) => addDays(startDate, i));
};

// Calculate appropriate number of days to show based on screen width
export const calculateDaysToShow = (containerWidth: number): number => {
  if (containerWidth < 480) {  // Very small screens
    return 2;
  } else if (containerWidth < 640) {  // Mobile
    return 3;
  } else if (containerWidth < 768) {  // Small tablet
    return 5;
  } else if (containerWidth < 1024) {  // Large tablet
    return 7;
  } else if (containerWidth < 1280) {  // Small desktop
    return 10;
  } else if (containerWidth < 1536) {  // Medium desktop
    return 14;
  } else {  // Large desktop
    return 21;
  }
};

// Calculate cell width based on container width and days to show
export const calculateCellWidth = (containerWidth: number, daysToShow: number): number => {
  return containerWidth / daysToShow;
};

// Get the first upcoming event from a list of events
export const findFirstUpcomingEvent = (events: Event[]): Event | null => {
  if (!events || events.length === 0) return null;
  
  const today = new Date();
  
  // Find the closest upcoming event
  const upcomingEvents = events.filter(event => {
    const eventStartDate = parseISO(event.dateStart);
    return isAfter(eventStartDate, today) || isSameDayHelper(eventStartDate, today);
  });
  
  if (upcomingEvents.length > 0) {
    // Sort by date ascending
    upcomingEvents.sort((a, b) => {
      return parseISO(a.dateStart).getTime() - parseISO(b.dateStart).getTime();
    });
    
    // The first event is the closest upcoming event
    return upcomingEvents[0];
  } else {
    // If no upcoming events, take the most recent past event
    const pastEvents = events.filter(event => {
      return isBefore(parseISO(event.dateStart), today);
    });
    
    if (pastEvents.length > 0) {
      // Sort by date descending
      pastEvents.sort((a, b) => {
        return parseISO(b.dateStart).getTime() - parseISO(a.dateStart).getTime();
      });
      
      // The first event is the most recent past event
      return pastEvents[0];
    }
  }
  
  return null;
};

// Create a default date range filter if none is provided
export const createDefaultDateRangeFilter = (startDate: Date, endDate: Date) => {
  return {
    startDate: startDate.toISOString(),
    endDate: addMonths(endDate, 3).toISOString(), // Show events 3 months ahead
  };
};
